import { makeAutoObservable, runInAction } from 'mobx'
import { CanceledError } from 'axios'
import { RequestCancelAxios } from 'shared/api/lib'
import {
  type ICallHistoryCall,
  type IFiltersList,
  type IParamsGetCallHistoryStatistic,
  type IResponseGetCallHistoryStatistic,
} from 'entities/CallHistory/api/types'
import { CallHistoryApi } from 'entities/CallHistory/api/callHistory'
import { type IReadMessage } from 'entities/Conversation/api/types'
import { CallFilter } from 'pages/conversations/pages/calls/ui/CallHistoryFilters/store/CallFilter'
import { type ICallFilter } from 'pages/conversations/pages/calls/ui/CallHistoryFilters/store/type'

const filters: ICallFilter[] = [
  { type: 'all-calls', outcome: null },
  { type: 'inbound', outcome: 'answered' },
  { type: 'inbound', outcome: 'missed' },
  { type: 'inbound', outcome: 'voicemail' },
  { type: 'inbound', outcome: null, source: 'ai_calling_agent' },
  { type: 'outbound', outcome: 'answered' },
  { type: 'outbound', outcome: 'no_answer' },
  { type: 'outbound', outcome: 'voicemail' },
]

export class CallHistoryFiltersStore {
  private _loadCallHistory
  private _loading = false
  private _activeFilterKey = 'all-calls'
  private _filtersMap: Map<string, CallFilter> = new Map(
    filters.map((item) => {
      const filter = new CallFilter(item)
      return [filter.id, filter]
    })
  )
  private _cancelRequest = new RequestCancelAxios()

  constructor(loadCallHistory: () => Promise<void>) {
    makeAutoObservable(this)

    this._loadCallHistory = loadCallHistory
  }

  get loading() {
    return this._loading
  }

  get activeFilterKey() {
    return this._activeFilterKey
  }

  get activeFilter() {
    return this._filtersMap.get(this._activeFilterKey)
  }

  get allCallsFilter() {
    return this._filtersMap.get('all-calls')
  }

  get filters() {
    return Array.from(this._filtersMap.values())
  }

  get inboundFilters() {
    return this.filters.filter((item) => item.type === 'inbound')
  }

  get outboundFilters() {
    return this.filters.filter((item) => item.type === 'outbound')
  }

  get isActiveAllCalls() {
    return this.activeFilter?.type === 'all-calls'
  }

  setActiveFilter = (value: string) => {
    this._activeFilterKey = value
  }

  get filtersList() {
    if (this.activeFilter && !this.isActiveAllCalls) {
      return [
        {
          filters: [
            { key: 'call.type', operator: 'equals', value: this.activeFilter.type },
            ...(this.activeFilter.outcome
              ? [{ key: 'call.outcome', operator: 'equals', value: this.activeFilter.outcome }]
              : []),
            ...(this.activeFilter.source
              ? [{ key: 'message.source', operator: 'equals', value: this.activeFilter.source }]
              : []),
          ],
        },
      ] as IFiltersList[]
    }

    return []
  }

  get hasCalls() {
    return this.allCallsFilter && this.allCallsFilter.count > 0
  }

  setData = ({ data }: IResponseGetCallHistoryStatistic) => {
    const { count, unread_count, groups } = data

    this.allCallsFilter?.setCount(count)
    this.allCallsFilter?.setUnreadCount(unread_count)

    Object.entries(groups).forEach(([type, { groups }]) => {
      Object.entries(groups).forEach(([outcome, { unread_count }]) => {
        const filter = this._filtersMap.get(`${type}.${outcome}`)
        if (filter) {
          filter.setUnreadCount(unread_count)
        }
      })
    })
  }

  loadData = async (params: IParamsGetCallHistoryStatistic) => {
    this.reset()

    try {
      this._cancelRequest.init()

      runInAction(() => {
        this._loading = true
      })

      const { data } = await CallHistoryApi.getCallHistoryStatistic(params, {
        cancelToken: this._cancelRequest.token,
      })

      this.setData(data)

      if (data.data.count > 0) {
        this._loadCallHistory()
      }

      return data
    } catch (e) {
      runInAction(() => {
        this._loading = e instanceof CanceledError
      })
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  increaseCount = ({ type, outcome, source }: ICallHistoryCall) => {
    this.allCallsFilter?.increaseUnreadCount()
    if (source === 'ai_calling_agent' && type) {
      this._filtersMap.get(`${type}.${source}`)?.increaseUnreadCount()
    }

    const filter = this._filtersMap.get(`${type}.${outcome}`)
    if (filter) {
      filter.increaseUnreadCount()
    }
  }

  decreaseCount = (data: IReadMessage[]) => {
    data.forEach(({ type, direction, status, source }) => {
      if (type === 'call') {
        this.allCallsFilter?.decreaseUnreadCount()

        if (source === 'ai_calling_agent' && direction) {
          this._filtersMap.get(`${direction}.${source}`)?.decreaseUnreadCount()
        }

        const filter = this._filtersMap.get(`${direction}.${status}`)
        if (filter) {
          filter.decreaseUnreadCount()
        }
      }
    })
  }

  resetAllCount = () => {
    this._filtersMap.forEach((filter) => filter.reset())
  }

  reset = () => {
    this.resetAllCount()
    this._activeFilterKey = 'all-calls'
    this._cancelRequest.reset()
  }
}
